import React, { useState, useEffect } from 'react';
import { players as playersApi, uploads as uploadsApi, stats as statsApi, auth as authApi } from '../utils/api';
import Modal from '../components/Modal';
import { useToast } from '../components/Toast';

// Default nutrition goals — shown as placeholders and used if left blank
const GOAL_DEFAULTS = {
  goal_calories:  2000,
  goal_protein_g: 150,
  goal_carbs_g:   300,
  goal_fat_g:     65,
  goal_sodium_mg: 2300,
  goal_sugar_g:   50,
  goal_fiber_g:   38,
};

const GOAL_FIELDS = [
  { key: 'goal_calories',  label: 'Calories',   unit: 'kcal', color: 'var(--accent-orange)'  },
  { key: 'goal_protein_g', label: 'Protein',    unit: 'g',    color: 'var(--accent-cyan)'    },
  { key: 'goal_carbs_g',   label: 'Carbs',      unit: 'g',    color: 'var(--accent-gold)'    },
  { key: 'goal_fat_g',     label: 'Fat',        unit: 'g',    color: 'var(--accent-purple)'  },
  { key: 'goal_sodium_mg', label: 'Sodium',     unit: 'mg',   color: 'var(--accent-red)'     },
  { key: 'goal_sugar_g',   label: 'Sugar',      unit: 'g',    color: 'var(--accent-magenta)' },
  { key: 'goal_fiber_g',   label: 'Fiber',      unit: 'g',    color: 'var(--accent-green)'   },
];

const emptyForm = () => ({
  name: '', age: '', title: '', feet: '', inches: '', weight_lbs: '', picture: '',
  ...Object.fromEntries(Object.entries(GOAL_DEFAULTS).map(([k, v]) => [k, String(v)])),
});

export default function PlayersManage({ activePlayer, activePlayerId, players, refreshPlayers }) {
  const toast = useToast();
  const [showModal,   setShowModal]  = useState(false);
  const [goalsTab,    setGoalsTab]   = useState(false); // false = profile tab, true = goals tab
  const [uploading,   setUploading]  = useState(false);
  const [leaderboard, setLeaderboard]= useState([]);
  const [showDelete,  setShowDelete] = useState(false);
  const [form,        setForm]       = useState(emptyForm());

  // Load leaderboard whenever player list changes (shows when 2+ players exist)
  useEffect(() => {
    if (players.length < 2) return;
    statsApi.leaderboard().then(setLeaderboard).catch(console.error);
  }, [players]);

  // Populate form when activePlayer loads or changes
  useEffect(() => {
    if (!activePlayer) return;
    const t = activePlayer.height_inches || 0;
    setForm({
      name:       activePlayer.name       || '',
      age:        activePlayer.age        != null ? String(activePlayer.age) : '',
      title:      activePlayer.title      || '',
      feet:       t ? String(Math.floor(t / 12)) : '',
      inches:     t ? String(Math.round(t % 12)) : '',
      weight_lbs: activePlayer.weight_lbs != null ? String(activePlayer.weight_lbs) : '',
      picture:    activePlayer.picture    || '',
      // Nutrition goals — use player's saved values, fall back to defaults
      goal_calories:  String(activePlayer.goal_calories  || GOAL_DEFAULTS.goal_calories),
      goal_protein_g: String(activePlayer.goal_protein_g || GOAL_DEFAULTS.goal_protein_g),
      goal_carbs_g:   String(activePlayer.goal_carbs_g   || GOAL_DEFAULTS.goal_carbs_g),
      goal_fat_g:     String(activePlayer.goal_fat_g     || GOAL_DEFAULTS.goal_fat_g),
      goal_sodium_mg: String(activePlayer.goal_sodium_mg || GOAL_DEFAULTS.goal_sodium_mg),
      goal_sugar_g:   String(activePlayer.goal_sugar_g   || GOAL_DEFAULTS.goal_sugar_g),
      goal_fiber_g:   String(activePlayer.goal_fiber_g   || GOAL_DEFAULTS.goal_fiber_g),
    });
  }, [activePlayer]);

  const openModal = (tab = false) => {
    setGoalsTab(tab);
    setShowModal(true);
  };

  const handleImg = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const r = await uploadsApi.upload(file);
      setForm(f => ({ ...f, picture: r.url }));
      toast('Image uploaded!', 'success');
    } catch (err) {
      toast('Upload failed: ' + err.message, 'error');
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { toast('Display name is required', 'error'); return; }

    const hi = (form.feet !== '' || form.inches !== '')
      ? parseFloat(form.feet || 0) * 12 + parseFloat(form.inches || 0)
      : null;

    const payload = {
      name:          form.name.trim(),
      age:           form.age        ? parseInt(form.age)        : null,
      title:         form.title,
      height_inches: hi,
      weight_lbs:    form.weight_lbs ? parseFloat(form.weight_lbs) : null,
      picture:       form.picture,
      // Nutrition goals — parse each, fall back to default if blank/invalid
      ...Object.fromEntries(
        GOAL_FIELDS.map(({ key }) => [
          key,
          parseInt(form[key]) || GOAL_DEFAULTS[key],
        ])
      ),
    };

    try {
      await playersApi.update(activePlayerId, payload);
      toast('Profile saved!', 'success');
      setShowModal(false);
      refreshPlayers();
    } catch (e) { toast(e.message, 'error'); }
  };

  const deleteAccount = async () => {
    try {
      await playersApi.delete(activePlayerId);
      authApi.logout();
    } catch (e) { toast(e.message, 'error'); }
  };

  const RANK_ICONS = ['🥇', '🥈', '🥉'];

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">👤 My Profile</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={() => openModal(true)}>🎯 Nutrition Goals</button>
          <button className="btn btn-primary" onClick={() => openModal(false)}>✎ Edit Profile</button>
        </div>
      </div>

      {/* ── Profile card ─────────────────────────────────────────────────── */}
      {activePlayer && (
        <div className="card player-hq-card" style={{ marginBottom: 24 }}>
          <div className="player-hq-avatar">
            {activePlayer.picture
              ? <img src={activePlayer.picture} alt="" />
              : <span>{activePlayer.name?.[0]}</span>
            }
          </div>
          <div className="player-hq-info">
            <h3 className="player-hq-name">{activePlayer.name}</h3>
            {activePlayer.title && <div className="player-hq-title">{activePlayer.title}</div>}
            <div className="player-hq-stats">
              {activePlayer.age           && <span>Age {activePlayer.age}</span>}
              {activePlayer.height_inches && (
                <span>{Math.floor(activePlayer.height_inches / 12)}'{Math.round(activePlayer.height_inches % 12)}"</span>
              )}
              {activePlayer.weight_lbs   && <span>{activePlayer.weight_lbs} lbs</span>}
            </div>
            {activePlayer.username && (
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: 6 }}>
                @{activePlayer.username}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Nutrition goals summary ───────────────────────────────────────── */}
      {activePlayer && (
        <>
          <div className="section-label">🎯 Nutrition Goals</div>
          <div className="card" style={{ marginBottom: 24 }}>
            <div className="goals-grid">
              {GOAL_FIELDS.map(({ key, label, unit, color }) => {
                const value = activePlayer[key] || GOAL_DEFAULTS[key];
                return (
                  <div key={key} className="goal-item">
                    <div className="goal-value" style={{ color }}>{value}<span className="goal-unit">{unit}</span></div>
                    <div className="goal-label">{label}</div>
                  </div>
                );
              })}
            </div>
            <button
              className="btn btn-sm"
              style={{ marginTop: 14 }}
              onClick={() => openModal(true)}
            >
              ✎ Edit Goals
            </button>
          </div>
        </>
      )}

      {/* ── Leaderboard (2+ players) ─────────────────────────────────────── */}
      {leaderboard.length >= 2 && (
        <>
          <div className="section-label">🏆 Leaderboard</div>
          <div className="card" style={{ marginBottom: 24, overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr><th>#</th><th>Player</th><th>Balance</th><th>Earned</th><th>Quests</th></tr>
              </thead>
              <tbody>
                {leaderboard.map((p, i) => (
                  <tr key={p.id} style={{ background: p.id === activePlayerId ? 'rgba(0,229,255,0.04)' : '' }}>
                    <td>
                      <span style={{
                        fontFamily: 'var(--font-display)', fontSize: '1rem', fontWeight: 700,
                        color: ['var(--accent-gold)', '#c0c0c0', '#cd7f32'][i] || 'var(--text-muted)',
                      }}>
                        {RANK_ICONS[i] || (i + 1)}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        {p.picture
                          ? <img src={p.picture} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', border: '1px solid var(--border-color)' }} />
                          : <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--bg-input)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-display)', fontSize: '0.7rem', color: 'var(--accent-cyan)', border: '1px solid var(--border-color)' }}>{p.name[0]}</div>
                        }
                        <div>
                          <div style={{ fontWeight: 600, color: p.id === activePlayerId ? 'var(--accent-cyan)' : 'var(--text-primary)' }}>
                            {p.name}{p.id === activePlayerId ? ' (you)' : ''}
                          </div>
                          {p.title && <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.6rem', color: 'var(--text-muted)' }}>{p.title}</div>}
                        </div>
                      </div>
                    </td>
                    <td style={{ fontFamily: 'var(--font-display)', color: 'var(--accent-gold)', fontWeight: 700 }}>⭐ {p.points_balance}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent-green)', fontSize: '0.8rem' }}>+{p.points_earned}</td>
                    <td style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{p.quests_completed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ── Danger zone ──────────────────────────────────────────────────── */}
      <div className="section-label" style={{ color: 'var(--accent-red)' }}>⚠ Danger Zone</div>
      <div className="card" style={{ borderColor: 'rgba(255,34,68,0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Delete Account</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: 'var(--text-muted)' }}>
              Permanently deletes your profile, quests, food logs, and budget data.
            </div>
          </div>
          <button className="btn btn-danger" onClick={() => setShowDelete(true)}>Delete My Account</button>
        </div>
      </div>

      {/* ── Edit Profile / Nutrition Goals Modal ──────────────────────────── */}
      {showModal && (
        <Modal
          title={goalsTab ? '🎯 Nutrition Goals' : '✎ Edit Profile'}
          onClose={() => setShowModal(false)}
          footer={
            <>
              <button className="btn" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save}>Save Changes</button>
            </>
          }
        >
          {/* Tab switcher */}
          <div className="modal-tabs">
            <button
              className={`modal-tab${!goalsTab ? ' active' : ''}`}
              onClick={() => setGoalsTab(false)}
            >
              👤 Profile
            </button>
            <button
              className={`modal-tab${goalsTab ? ' active' : ''}`}
              onClick={() => setGoalsTab(true)}
            >
              🎯 Nutrition Goals
            </button>
          </div>

          {/* ── Profile tab ── */}
          {!goalsTab && (
            <>
              <div className="form-group">
                <label className="form-label">Display Name *</label>
                <input className="form-input" value={form.name} autoFocus
                  onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>

              <div className="form-group">
                <label className="form-label">Profile Picture</label>
                {form.picture && (
                  <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <img src={form.picture} alt="" style={{ width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '2px solid var(--accent-cyan)' }} />
                    <button className="btn btn-sm btn-danger" onClick={() => setForm({ ...form, picture: '' })}>Remove</button>
                  </div>
                )}
                <label className="upload-btn">
                  {uploading ? '⏳ Uploading...' : '📁 Choose Image File'}
                  <input type="file" accept="image/*" onChange={handleImg} style={{ display: 'none' }} />
                </label>
                <div style={{ margin: '8px 0', fontFamily: 'var(--font-mono)', fontSize: '0.62rem', color: 'var(--text-muted)', textAlign: 'center' }}>
                  — or paste URL —
                </div>
                <input className="form-input" value={form.picture}
                  onChange={e => setForm({ ...form, picture: e.target.value })} placeholder="https://..." />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Age</label>
                  <input className="form-input" type="number" min="1" max="120" value={form.age}
                    onChange={e => setForm({ ...form, age: e.target.value })} placeholder="Years" />
                </div>
                <div className="form-group">
                  <label className="form-label">Title / Role</label>
                  <input className="form-input" value={form.title}
                    onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Software Engineer" />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Height (optional)</label>
                <div className="form-row">
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input className="form-input" type="number" min="0" max="8" value={form.feet}
                      onChange={e => setForm({ ...form, feet: e.target.value })} placeholder="0" />
                    <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', fontSize: '0.85rem' }}>ft</span>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <input className="form-input" type="number" min="0" max="11" value={form.inches}
                      onChange={e => setForm({ ...form, inches: e.target.value })} placeholder="0" />
                    <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', fontSize: '0.85rem' }}>in</span>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Weight (lbs, optional)</label>
                <input className="form-input" type="number" step="0.1" min="0" value={form.weight_lbs}
                  onChange={e => setForm({ ...form, weight_lbs: e.target.value })} placeholder="lbs" />
              </div>
            </>
          )}

          {/* ── Nutrition goals tab ── */}
          {goalsTab && (
            <>
              <p style={{ fontFamily: 'var(--font-mono)', fontSize: '0.7rem', color: 'var(--text-muted)', marginBottom: 16, lineHeight: 1.6 }}>
                Set your personal daily targets. These control the progress bars in the Food Log and Player HQ.
              </p>
              <div className="nutrient-grid">
                {GOAL_FIELDS.map(({ key, label, unit, color }) => (
                  <div className="form-group" key={key}>
                    <label className="form-label" style={{ color }}>
                      {label} ({unit})
                    </label>
                    <input
                      className="form-input"
                      type="number"
                      min="0"
                      step={unit === 'kcal' || unit === 'mg' ? '1' : '1'}
                      value={form[key]}
                      onChange={e => setForm({ ...form, [key]: e.target.value })}
                      placeholder={String(GOAL_DEFAULTS[key])}
                    />
                  </div>
                ))}
              </div>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.62rem', color: 'var(--text-muted)', marginTop: 4 }}>
                Leave any field blank to use the app default.
              </div>
            </>
          )}
        </Modal>
      )}

      {/* ── Delete Confirmation Modal ─────────────────────────────────────── */}
      {showDelete && (
        <Modal
          title="⚠ Delete Account"
          onClose={() => setShowDelete(false)}
          footer={
            <>
              <button className="btn" onClick={() => setShowDelete(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={deleteAccount}>Yes, Delete Everything</button>
            </>
          }
        >
          <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 16 }}>⚠️</div>
            <p style={{ color: 'var(--text-primary)', marginBottom: 12 }}>
              This will permanently delete <strong>{activePlayer?.name}</strong>'s account and all data:
            </p>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.75rem', color: 'var(--accent-red)', lineHeight: 2 }}>
              All quests · All food logs · All budget items · All redemptions
            </div>
            <p style={{ color: 'var(--text-muted)', marginTop: 16, fontFamily: 'var(--font-mono)', fontSize: '0.7rem' }}>
              This action cannot be undone.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
}